import {
  Box,
  Heading,
  HStack,
  IconButton,
  Separator,
  Text,
  Timeline,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { FaBolt, FaEdit, FaEnvelope, FaFileAlt, FaLink } from 'react-icons/fa';
import type { LearnMoreSection } from '../LearnMore';
import useLearnMoreSections from '../hooks/useLearnMoreSections';

const SECTIONS: LearnMoreSection[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'content-tools', label: 'Content Tools' },
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'missing-a-feature', label: 'Missing a feature?' },
];

const TIMELINE_ICON_SIZE = 12;

const CONTENT_MANAGEMENT_BULLETS = [
  {
    title: 'Custom Website Templates',
    description:
      'Choose from professionally designed, mobile-friendly layouts created for clubs. Add your colours, logo, imagery, and content to match your club’s identity.',
  },
  {
    title: 'Club Blog / News',
    description:
      'Publish updates and announcements directly from the admin area of your own website. Posts appear instantly, so members always see the latest information.',
  },
  {
    title: 'Document Hosting',
    description:
      'Store and display codes of conduct, AGM minutes, constitutions, policies, safety documents, and training guides in one place.',
  },
  {
    title: 'Built-In Contact Form (With Bot Protection)',
    description:
      'Includes a secure Contact Us form with built-in spam protection. Route specific enquiry types to the right volunteer—like welfare questions straight to your Welfare Officer.',
  },
  {
    title: 'Mailchimp & Instagram Integration',
    description:
      "Sync members to Mailchimp for newsletters and display your latest Instagram posts directly on your club's website homepage.",
  },
  {
    title: 'Create sessions',
    description:
      'Members which you assign to have coach permissions can create sessions and events which then appear on the session booking app. The sessions also automatically populate the club sessions page so prospective members can see what is on offer.',
  },
  {
    title: 'Club volunteers',
    description:
      'When you set a member as a club coach the coaches page is automatically populated with their name and profile picture.',
  },
];

const GETTING_STARTED_STEPS = [
  {
    icon: <FaEnvelope size={TIMELINE_ICON_SIZE} />,
    title: '1. Contact Olympiq',
    body: `Visit the Contact Us page, drop us a message, and share a few headline details about your club. We’ll confirm timelines, onboarding, and who should be involved in the first content pass. Typically we can have your site and mobile app up and running in a matter of days.`,
  },
  {
    icon: <FaFileAlt size={TIMELINE_ICON_SIZE} />,
    title: '2. Complete the Content Brief',
    body: `We email you a simple document that captures colours, imagery, copy, and any documents needed for launch. Fill it in, attach assets, and send it back—no design tools required. We can help you along the way.`,
  },
  {
    icon: <FaEdit size={TIMELINE_ICON_SIZE} />,
    title: '3. Edit Content in Your Admin Dashboard',
    body: `Once the first version is live, your club admins can log in to the dashboard on your own website to edit pages, news posts, sessions, and documents whenever they like.`,
  },
  {
    icon: <FaBolt size={TIMELINE_ICON_SIZE} />,
    title: '4. Enjoy Instant, Synced Publishing',
    body: `Updates go live in seconds across both the mobile app and website. Prospective members always see consistent information about what they’re joining.`,
  },
];

const ContentManagement = (): React.JSX.Element => {
  useLearnMoreSections(SECTIONS, { pageTitle: 'Content Management' });

  return (
    <VStack align="stretch" gap={12}>
      <Box>
        <Heading size="3xl" letterSpacing="-0.04em">
          Content management
        </Heading>
        <Text marginTop={4} color="gray.600" fontSize="lg" maxW="3xl" _dark={{ color: 'gray.300' }}>
          Need to keep your club website updated but don&apos;t have a webmaster to hand? Olympiq
          builds and manages your club website and mobile app enabling club admins to publish
          consistent content within minutes just in one place.
        </Text>
        <Text marginTop={2} color="gray.600" fontSize="lg" maxW="3xl" _dark={{ color: 'gray.300' }}>
          The same platform that handles payments, membership renewals, and sessions also powers
          your pages, documents, and announcements - removing double entry work.
        </Text>
      </Box>

      <Separator />

      <SectionBlock id="overview" title="Overview">
        <Text>
          When you sign up to Olympiq, your club receives a fully customisable website designed to
          work seamlessly with your membership system. Members sign up, renew, create sessions, and
          much more on your website - not a third party. One system, one login, and all the content
          stays in sync.
        </Text>
        <Text>
          All you need to do is bring the domain{' '}
          <Text as="span" fontStyle="italic">
            (my-club.org)
          </Text>{' '}
          and Olympiq will handle the rest. There&apos;s no technical work for you to do - and no
          need to find a club webmaster to keep news and documents up to date.
        </Text>
      </SectionBlock>

      <SectionBlock id="content-tools" title="Content Tools">
        <Text marginBottom={4}>
          We keep adding new ways to update your pages. Here are a few tools clubs rely on every
          day:
        </Text>
        <VStack as="ul" gap={4} align="stretch" margin={0} padding={0} listStyleType="none">
          {CONTENT_MANAGEMENT_BULLETS.map((bullet) => (
            <HStack as="li" key={bullet.title} align="start" gap={4}>
              <Box
                as="span"
                fontWeight="bold"
                fontSize="sm"
                minW={{ base: '180px', md: '220px' }}
                maxW={{ base: '180px', md: '220px' }}
                flexShrink={0}
              >
                {bullet.title}
              </Box>
              <Text color="gray.700" _dark={{ color: 'gray.300' }}>
                {bullet.description}
              </Text>
            </HStack>
          ))}
        </VStack>
      </SectionBlock>

      <SectionBlock id="getting-started" title="Getting Started">
        <Text>
          Content management sits on the same reliable deployment workflow as the rest of the
          platform. Here&apos;s how we keep launches and major updates stress-free:
        </Text>
        <Timeline.Root colorPalette="blue" size="lg" marginTop={6}>
          {GETTING_STARTED_STEPS.map((step) => (
            <Timeline.Item key={step.title}>
              <Timeline.Connector>
                <Timeline.Separator />
                <Timeline.Indicator bg="gray.800" color="white">
                  {step.icon}
                </Timeline.Indicator>
              </Timeline.Connector>
              <Timeline.Content>
                <Timeline.Title fontSize="md" fontWeight="semibold">
                  {step.title}
                </Timeline.Title>
                <Timeline.Description
                  color="gray.600"
                  _dark={{ color: 'gray.300' }}
                  whiteSpace="pre-line"
                >
                  {step.body}
                </Timeline.Description>
              </Timeline.Content>
            </Timeline.Item>
          ))}
        </Timeline.Root>
      </SectionBlock>

      <SectionBlock id="missing-a-feature" title="Missing a feature?">
        <Text>
          Let us know if there&apos;s a feature you&apos;d like to see. We&apos;re always open to
          feature requests.
        </Text>
      </SectionBlock>
    </VStack>
  );
};

interface SectionBlockProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

const SectionBlock = ({ id, title, children }: SectionBlockProps): React.JSX.Element => (
  <Box as="section" id={id} scrollMarginTop="120px">
    <Heading size="2xl" fontWeight="bold" marginBottom={3}>
      {title}
      <IconButton
        aria-label="Copy link"
        variant="ghost"
        size="xs"
        onClick={() => {
          navigator.clipboard.writeText(window.location.href);
        }}
      >
        <FaLink />
      </IconButton>
    </Heading>
    <VStack align="stretch" gap={3}>
      {typeof children === 'string' ? <Text color="gray.600">{children}</Text> : children}
    </VStack>
  </Box>
);

export default ContentManagement;
